import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { documentService, getErrorMessage } from '../../../services/apiService';

/**
 * What a document said, beside what the vendor record says.
 *
 * An admin uploading on a vendor's behalf gets the document read straight away. Each value is
 * shown against the column it belongs to: an empty column it can fill, a value it agrees with,
 * or one it disagrees with. Nothing reaches the vendor record until the admin ticks to say they
 * have checked it — automatic reading can mistake one character for another, and the person who
 * accepts a value owns it.
 *
 * A value that disagrees with the record is red, and what the server warns about — not the
 * document at all, out of date, a number that is not the vendor's — sits at the top in the same
 * tone. The reader's confidence is never shown as a number: a percentage reads as a verdict.
 */

const LOW_CONFIDENCE = 80;

const ERROR_TONE = 'border-destructive/30 bg-destructive/5 text-destructive';

const STATUS = {
  fill: { icon: 'Plus', label: 'Will fill in', row: 'border-primary/30 bg-primary/5', chip: 'border-primary/30 bg-primary/10 text-primary' },
  conflict: { icon: 'AlertTriangle', label: 'Does not match the record', row: 'border-destructive/30 bg-destructive/5', chip: ERROR_TONE },
  match: { icon: 'Check', label: 'Matches', row: 'border-border bg-muted/20', chip: 'border-success/30 bg-success/5 text-success' },
  locked: { icon: 'AlertTriangle', label: 'Does not match the verified record', row: 'border-destructive/30 bg-destructive/5', chip: ERROR_TONE },
};

/** One line per warning, at the top. Never disables anything below it. */
function Warnings({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <div role="alert" className={`mb-3 flex flex-col gap-1 rounded-md border px-3 py-2 text-sm ${ERROR_TONE}`}>
      {warnings.map((w) => (
        <p key={`${w.code}:${w.message}`} className="flex items-start gap-2">
          <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
          <span>{w.message}</span>
        </p>
      ))}
    </div>
  );
}

export default function DocumentReadReview({ read, onApplied, className = '' }) {
  const [fields, setFields] = useState(read?.fields || []);
  const [accepted, setAccepted] = useState(() => new Set((read?.fields || []).filter((f) => f.status === 'fill').map((f) => f.column)));
  const [confirmed, setConfirmed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const selectable = useMemo(() => fields.filter((f) => f.status === 'fill' || f.status === 'conflict'), [fields]);
  const unsure = useMemo(
    () => fields.filter((f) => accepted.has(f.column) && typeof f.confidence === 'number' && f.confidence < LOW_CONFIDENCE),
    [fields, accepted]
  );

  if (!read) return null;

  const toggle = (column) => {
    setConfirmed(false);
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await documentService.applyRead(read.document_id, [...accepted]);
      const data = res?.data;
      setFields(data?.fields || fields);
      setAccepted(new Set());
      setConfirmed(false);
      setDone(data?.applied || []);
      if (onApplied) onApplied(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className={`rounded-lg border border-border bg-muted/20 p-3 ${className}`}>
      <Warnings warnings={read.warnings} />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon name="ScanText" size={16} className="text-primary" />
            We read this {String(read.label || 'document').toLowerCase()}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            {read.summary?.applicable > 0
              ? `${read.summary.applicable} value${read.summary.applicable === 1 ? '' : 's'} differ from the vendor record — tick what should be written.`
              : 'Everything on it already matches the vendor record.'}
            {read.confirmed_by && <> Confirmed by {read.confirmed_by}.</>}
          </p>
        </div>
        {selectable.length > 1 && (
          <button
            type="button"
            onClick={() => { setConfirmed(false); setAccepted(accepted.size === selectable.length ? new Set() : new Set(selectable.map((f) => f.column))); }}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            {accepted.size === selectable.length ? 'Clear all' : 'Select all'}
          </button>
        )}
      </div>

      <ul className="mt-3 flex flex-col gap-2">
        {fields.map((f) => {
          const s = STATUS[f.status] || STATUS.match;
          const pickable = f.status === 'fill' || f.status === 'conflict';
          const differs = f.status === 'conflict' || f.status === 'locked';
          return (
            <li key={f.column} className={`flex items-start gap-3 rounded-lg border p-3 ${s.row}`}>
              {pickable ? (
                <input
                  type="checkbox"
                  checked={accepted.has(f.column)}
                  onChange={() => toggle(f.column)}
                  className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
                  aria-label={`Write ${f.label} to the vendor`}
                />
              ) : (
                <Icon name={s.icon} size={16} className={`mt-0.5 shrink-0 ${differs ? 'text-destructive' : 'text-text-secondary'}`} />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-sm font-medium text-foreground">{f.label}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.chip}`}>
                    <Icon name={s.icon} size={10} />
                    {s.label}
                  </span>
                </div>
                <p className={`mt-1 break-words font-mono text-sm ${differs ? 'text-destructive' : 'text-foreground'}`}>
                  {differs && <span className="mr-1 font-sans text-xs text-text-secondary">On the document:</span>}
                  {f.value}
                </p>
                {f.current && f.status !== 'match' && (
                  <p className="mt-0.5 break-words text-xs text-text-secondary">
                    On the record: <span className="font-mono text-foreground">{f.current}</span>
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {error && (
        <p className="mt-3 flex items-center gap-2 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} /> {error}
        </p>
      )}

      {done && (
        <p className="mt-3 flex items-center gap-2 text-sm text-success">
          <Icon name="CheckCircle2" size={16} />
          {done.length > 0 ? `Updated ${done.length} field${done.length === 1 ? '' : 's'} on the vendor.` : 'Nothing left to update.'}
        </p>
      )}

      {accepted.size > 0 && (
        <div className="mt-3 rounded-lg border border-warning/40 bg-warning/5 p-3">
          <p className="flex items-start gap-2 text-xs text-foreground">
            <Icon name="AlertTriangle" size={14} className="mt-0.5 shrink-0 text-warning" />
            <span>
              <span className="font-medium">Check these against the document before confirming.</span> Automatic reading is not
              perfect — it can mistake one character for another
              {unsure.length > 0 && <>, and it was unsure about <span className="font-medium">{unsure.map((f) => f.label).join(', ')}</span></>}
              . This writes to the vendor record and is recorded against you.
            </span>
          </p>
          <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs font-medium text-foreground">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
            />
            I have checked the {accepted.size} value{accepted.size === 1 ? '' : 's'} above against the document and confirm{' '}
            {accepted.size === 1 ? 'it is' : 'they are'} correct.
          </label>
          <Button
            variant="default"
            onClick={apply}
            loading={applying}
            disabled={applying || !confirmed}
            iconName="Wand2"
            iconPosition="left"
            className="mt-3"
            title={confirmed ? undefined : 'Confirm the values are correct first'}
          >
            Write {accepted.size} field{accepted.size === 1 ? '' : 's'} to the vendor
          </Button>
        </div>
      )}
    </div>
  );
}
